import config from 'config';

const COGNITO_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';

interface CognitoTokens {
    idToken: string;
    accessToken: string;
    refreshToken: string;
    username: string;
}

export type SetSessionResult = { ok: true } | { ok: false; reason: 'missing_config' | 'pool_mismatch' | 'storage_failed' };

const getPoolIdFromIss = (iss: string): string | null => {
    if (!iss || typeof iss !== 'string') return null;
    const parts = iss.split('/');
    return parts.length > 0 ? parts[parts.length - 1] : null;
};

const decodePayload = (token: string): Record<string, unknown> | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padding = base64.length % 4;
        if (padding) base64 += '='.repeat(4 - padding);
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch {
        return null;
    }
};

export const setCognitoSessionFromTokens = ({ idToken, accessToken, refreshToken, username }: CognitoTokens): SetSessionResult => {
    const clientId = config.cognito?.APP_CLIENT_ID;
    const frontendPoolId = config.cognito?.USER_POOL_ID;
    const identityPoolId = config.cognito?.IDENTITY_POOL_ID;
    if (!clientId || !username) return { ok: false, reason: 'missing_config' };

    const payload = decodePayload(idToken);
    if (payload?.iss) {
        const tokenPoolId = getPoolIdFromIss(payload.iss as string);
        if (frontendPoolId && tokenPoolId && tokenPoolId !== frontendPoolId) {
            return { ok: false, reason: 'pool_mismatch' };
        }
    }

    if (identityPoolId) {
        const identityIdKey = `com.amplify.Cognito.${identityPoolId}.identityId`;
        try {
            sessionStorage.removeItem(identityIdKey);
        } catch {}
    }

    const prefix = `${COGNITO_STORAGE_PREFIX}.${clientId}.${username}`;
    try {
        sessionStorage.setItem(`${prefix}.idToken`, idToken);
        sessionStorage.setItem(`${prefix}.accessToken`, accessToken);
        sessionStorage.setItem(`${prefix}.refreshToken`, refreshToken);
        sessionStorage.setItem(`${COGNITO_STORAGE_PREFIX}.${clientId}.LastAuthUser`, username);
        return { ok: true };
    } catch {
        return { ok: false, reason: 'storage_failed' };
    }
};
